import { BacktesterScenario } from '../../../../../providers/backtesting/api/backtester-config-api.types';
import {
  METRICS,
  MetricData,
  MetricKey,
} from '../../../../../providers/portfolio-performance/risk/risk.types';

// The pnl_reports field each risk metric maps to. This is the natural-fit part
// of the reuse plan (§3): a backtest already carries delta/theta/gamma/vega/pv
// per snapshot, so the live risk panel's [timestamp, value][] series come
// straight out of the run with no extra fetch.
const FIELD_BY_METRIC: Record<MetricKey, string> = {
  PV: 'pv',
  DELTA: 'delta',
  GAMMA: 'gamma',
  THETA: 'theta',
  VEGA: 'vega',
};

const toEpochSeconds = (timestamp: string): number | null => {
  // Snapshot timestamps are zoneless UTC ("YYYY-MM-DD HH:mm:ss"); normalise to
  // ISO before parsing. RiskPanelGraph / lightweight-charts want seconds.
  const ms = new Date(timestamp.replace(' ', 'T')).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
};

// Reshapes one backtest scenario into the Record<MetricKey, MetricData> shape
// RiskPanelCard reads off RiskContext, so the shared risk panel renders
// verbatim over backtest greeks. Series are sorted ascending and deduped to
// one point per second (lightweight-charts requires strictly increasing,
// unique timestamps); a metric with no usable points is null so the panel
// shows its own "No data available" state.
export const buildRiskMetrics = (
  scenario: BacktesterScenario | undefined,
): Record<MetricKey, MetricData> => {
  const rows = scenario?.pnl_reports ?? [];
  const result = {} as Record<MetricKey, MetricData>;

  for (const metric of METRICS) {
    const field = FIELD_BY_METRIC[metric];
    const byTime = new Map<number, number>();

    for (const row of rows) {
      const ts = toEpochSeconds(row.timestamp);
      const raw = row[field];
      const value = typeof raw === 'number' ? raw : Number(raw);
      if (ts === null || !Number.isFinite(value)) continue;
      byTime.set(ts, value); // last write per second wins
    }

    const data = [...byTime.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([time, value]) => [time, value] as [number, number]);

    result[metric] = data.length
      ? { data, header: ['time', metric], organization: '', symbol: '', type: metric }
      : null;
  }

  return result;
};
